import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useListLeasesQuery } from '../../../store/api/leasesApi';
import { useListMaintenanceRequestsQuery } from '../../../store/api/maintenanceApi';
import { useListPaymentsQuery } from '../../../store/api/paymentsApi';
import { useGetUserQuery } from '../../../store/api/usersApi';

interface TenantDetailsModalProps {
	tenant: any;
	onClose: () => void;
}

const TenantDetailsModal: React.FC<TenantDetailsModalProps> = ({ tenant, onClose }) => {
  const [activeTab, setActiveTab] = useState<string>('profile');

  // Fetch real data for tenant
  const { data: userData } = useGetUserQuery(tenant?.id || '', { skip: !tenant?.id });
  const { data: leasesData } = useListLeasesQuery({ page_size: 1000 });
  const { data: paymentsData } = useListPaymentsQuery({ page_size: 1000 });
  const { data: maintenanceData } = useListMaintenanceRequestsQuery({ page_size: 1000 });

  // Get tenant's lease
  const tenantLease = useMemo(() => {
    if (!leasesData?.results || !tenant?.id) return null;
    return leasesData.results.find((l) => l.tenant_id === tenant.id && l.status === 'active') ||
           leasesData.results.find((l) => l.tenant_id === tenant.id);
  }, [leasesData, tenant?.id]);

  // Get tenant's payments
  const paymentHistory = useMemo(() => {
    if (!paymentsData?.results || !tenant?.id) return [];
    return paymentsData.results
      .filter((p) => p.tenant_id === tenant.id)
      .map((p) => ({
        date: p.paid_date || p.due_date || p.created_at,
        amount: Number(p.amount) || 0,
        status: p.status || 'pending',
        method: p.payment_method || 'N/A',
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 10);
  }, [paymentsData, tenant?.id]);

  // Get tenant's maintenance requests
  const maintenanceRequests = useMemo(() => {
    if (!maintenanceData?.results || !tenant?.id) return [];
    return maintenanceData.results
      .filter((m) => m.requested_by_id === tenant.id)
      .map((m) => ({
        id: m.id,
        date: m.created_at,
        issue: m.title || m.description || 'Maintenance Request',
        status: m.status || 'pending',
        priority: m.priority || 'medium',
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [maintenanceData, tenant?.id]);

  // Calculate payment statistics
  const paymentStats = useMemo(() => {
    const paid = paymentHistory.filter((p) => p.status === 'paid');
    const totalPaid = paid.reduce((sum, p) => sum + p.amount, 0);
    const outstanding = paymentHistory
      .filter((p) => p.status === 'pending' || p.status === 'overdue')
      .reduce((sum, p) => sum + p.amount, 0);

    // Calculate next due date
    let nextDue: string = 'N/A';

    // First, try to find the next pending/overdue payment
    const pendingPayments = paymentHistory
      .filter((p) => p.status === 'pending' || p.status === 'overdue')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (pendingPayments.length > 0 && pendingPayments[0].date) {
      try {
        const dueDate = new Date(pendingPayments[0].date);
        if (!isNaN(dueDate.getTime())) {
          nextDue = dueDate.toISOString();
        }
      } catch (e) {
        // Invalid date, continue to lease-based calculation
      }
    }

    // If no pending payments, calculate from lease start date
    if (nextDue === 'N/A' && tenantLease?.start_date) {
      try {
        const leaseStart = new Date(tenantLease.start_date);
        if (!isNaN(leaseStart.getTime())) {
          // Calculate next payment due (assuming monthly payments)
          const today = new Date();
          const monthsSinceStart =
            (today.getFullYear() - leaseStart.getFullYear()) * 12 +
            (today.getMonth() - leaseStart.getMonth());

          // Next due is the first of next month from lease start, adjusted for months passed
          const nextDueDate = new Date(leaseStart);
          nextDueDate.setMonth(leaseStart.getMonth() + monthsSinceStart + 1);
          nextDueDate.setDate(1); // First of the month

          if (!isNaN(nextDueDate.getTime())) {
            nextDue = nextDueDate.toISOString();
          }
        }
      } catch (e) {
        // Invalid date, keep as N/A
      }
    }

    return { totalPaid, outstanding, nextDue };
  }, [paymentHistory, tenantLease]);

  // Calculate maintenance statistics
  const maintenanceStats = useMemo(() => {
    const completed = maintenanceRequests.filter((m) => m.status === 'completed').length;
    const inProgress = maintenanceRequests.filter((m) => m.status === 'in_progress').length;
    const pending = maintenanceRequests.filter((m) => m.status === 'pending').length;
    return { completed, inProgress, pending };
  }, [maintenanceRequests]);

  const tabs: Array<{ id: string; label: string; icon: any }> = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'lease', label: 'Lease Details', icon: 'FileText' },
    { id: 'payments', label: 'Payment History', icon: 'CreditCard' },
    { id: 'maintenance', label: 'Maintenance', icon: 'Wrench' }
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | Date) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadge = (status: string, type: 'default' | 'maintenance' | 'priority' = 'default') => {
    const configs: Record<string, Record<string, { color: string; label: string }>> = {
      default: {
        paid: { color: 'bg-success/10 text-success', label: 'Paid' },
        pending: { color: 'bg-warning/10 text-warning', label: 'Pending' },
        overdue: { color: 'bg-error/10 text-error', label: 'Overdue' }
      },
      maintenance: {
        completed: { color: 'bg-success/10 text-success', label: 'Completed' },
        in_progress: { color: 'bg-primary/10 text-primary', label: 'In Progress' },
        pending: { color: 'bg-warning/10 text-warning', label: 'Pending' }
      },
      priority: {
        high: { color: 'bg-error/10 text-error', label: 'High' },
        medium: { color: 'bg-warning/10 text-warning', label: 'Medium' },
        low: { color: 'bg-success/10 text-success', label: 'Low' }
      }
    };

    const config = configs[type]?.[status] || configs.default.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={32} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">{tenant.name}</h3>
                <p className="text-muted-foreground">{tenant.email}</p>
                <p className="text-muted-foreground">{tenant.phone}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-foreground mb-3">Personal Information</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Full Name:</span>
                    <span className="text-foreground">{tenant.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email:</span>
                    <span className="text-foreground">{tenant.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Phone:</span>
                    <span className="text-foreground">{tenant.phone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Emergency Contact:</span>
                    <span className="text-foreground">{tenant.emergencyContact || 'Not provided'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-3">Tenancy Information</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Property:</span>
                    <span className="text-foreground">{tenant.propertyName || tenant.property || "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Unit:</span>
                    <span className="text-foreground">{tenant.unit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Move-in Date:</span>
                    <span className="text-foreground">{tenant.leaseStart}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="text-foreground">{tenant.status}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'lease':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-foreground mb-3">Lease Terms</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Date:</span>
                    <span className="text-foreground">{formatDate(tenantLease?.start_date || tenant.leaseStart)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">End Date:</span>
                    <span className="text-foreground">{formatDate(tenantLease?.end_date || tenant.leaseEnd)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monthly Rent:</span>
                    <span className="text-foreground">
                      {tenantLease?.monthly_rent
                        ? formatCurrency(Number(tenantLease.monthly_rent) || 0)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Security Deposit:</span>
                    <span className="text-foreground">
                      {tenantLease?.security_deposit
                        ? formatCurrency(Number(tenantLease.security_deposit) || 0)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Lease Status:</span>
                    <span className="text-foreground capitalize">{tenantLease?.status || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-3">Additional Details</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Lease Type:</span>
                    <span className="text-foreground">Fixed Term</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Renewal Option:</span>
                    <span className="text-foreground">Available</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pet Policy:</span>
                    <span className="text-foreground">No Pets</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Parking:</span>
                    <span className="text-foreground">1 Space Included</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-foreground mb-3">Lease Documents</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="FileText" size={20} className="text-primary" />
                    <span className="text-sm text-foreground">Lease Agreement.pdf</span>
                  </div>
                  <Button variant="ghost" size="sm" iconName="Download">
                    Download
                  </Button>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="FileText" size={20} className="text-primary" />
                    <span className="text-sm text-foreground">Move-in Checklist.pdf</span>
                  </div>
                  <Button variant="ghost" size="sm" iconName="Download">
                    Download
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-success/10 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                  <span className="text-sm font-medium text-success">Total Paid</span>
                </div>
                <p className="text-2xl font-bold text-success">{formatCurrency(paymentStats.totalPaid)}</p>
              </div>
              <div className="bg-warning/10 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Clock" size={20} className="text-warning" />
                  <span className="text-sm font-medium text-warning">Outstanding</span>
                </div>
                <p className="text-2xl font-bold text-warning">{formatCurrency(paymentStats.outstanding)}</p>
              </div>
              <div className="bg-primary/10 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Calendar" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-primary">Next Due</span>
                </div>
                <p className="text-2xl font-bold text-primary">{formatDate(paymentStats.nextDue)}</p>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-foreground mb-3">Payment History</h4>
              <div className="space-y-2">
                {paymentHistory.map((payment, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="CreditCard" size={20} className="text-primary" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(payment.date)} • {payment.method}</p>
                      </div>
                    </div>
                    {getStatusBadge(payment.status)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'maintenance':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-success/10 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                  <span className="text-sm font-medium text-success">Completed</span>
                </div>
                <p className="text-2xl font-bold text-success">{maintenanceStats.completed}</p>
              </div>
              <div className="bg-primary/10 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Clock" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-primary">In Progress</span>
                </div>
                <p className="text-2xl font-bold text-primary">{maintenanceStats.inProgress}</p>
              </div>
              <div className="bg-warning/10 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="AlertCircle" size={20} className="text-warning" />
                  <span className="text-sm font-medium text-warning">Pending</span>
                </div>
                <p className="text-2xl font-bold text-warning">{maintenanceStats.pending}</p>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-foreground mb-3">Maintenance Requests</h4>
              <div className="space-y-3">
                {maintenanceRequests.map((request) => (
                  <div key={request.id} className="p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{request.issue}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(request.date)}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(request.priority, 'priority')}
                        {getStatusBadge(request.status, 'maintenance')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1300 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden modal-shadow">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Tenant Details</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex space-x-0 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium border-b-2 smooth-transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default TenantDetailsModal;
